import React from "react";
import { DateTime } from "luxon";
import { cls } from "app/utils";
import { getPaymentIcon } from "app/utils/paymentUtils";
import { useTranslation } from "react-i18next";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { formatTime } from "app/utils/luxonFormats";

import { eventTint } from "app/utils/calendarColors";
import styles from "./styles.module.scss";

type PropsDef = {
  event: any;
  timeText: string;
  timeZone?: string;
};

function formatTimeRange(
  start: Date,
  end: Date,
  timeZone: string,
  timeFormat: "12h" | "24h",
): string {
  const startTime = formatTime(
    DateTime.fromJSDate(start, { zone: timeZone }),
    timeFormat,
  );
  const endTime = formatTime(
    DateTime.fromJSDate(end, { zone: timeZone }),
    timeFormat,
  );
  return `${startTime} - ${endTime}`;
}

function getPaymentLabel(
  paymentMethod: string,
  t: (key: string) => string,
): string | null {
  switch (paymentMethod) {
    case "online":
      return t("Online Payment");
    case "package":
      return t("Package");
    case "offline":
      return t("Offline Payment");
    default:
      return null;
  }
}

function getPaymentStatusClass(
  paymentStatus: string,
  expiresAt: string,
): string {
  if (paymentStatus === "past_due" || paymentStatus === "failed") {
    return styles.schedule_badge_failed;
  }
  if (paymentStatus === "pending" || expiresAt) {
    return styles.schedule_badge_pending;
  }
  return styles.schedule_badge_paid;
}

export default function ScheduleEvent(props: PropsDef) {
  const { t } = useTranslation();
  const { preferences } = useCalendarPreferences();
  const {
    eventColor,
    type,
    payment_method: paymentMethod,
    payment_status: paymentStatus,
    expires_at: expiresAt,
    registrationStatus,
    registrationStatusCode,
  } = props.event.extendedProps;

  const barColor = eventColor || "#E54500";
  // Shaded only when the booking option has a colour; clinics and blocks render
  // through here too and never are (see eventTint).
  const tint = eventTint(eventColor);
  const timeRange =
    props.event.start && props.event.end && props.timeZone
      ? formatTimeRange(
          props.event.start,
          props.event.end,
          props.timeZone,
          preferences.timeFormat,
        )
      : props.timeText;

  const paymentLabel = paymentMethod ? getPaymentLabel(paymentMethod, t) : null;

  const isProgram = type === "program";
  const isScheduleBlock = type === "coach_schedule_block";

  return (
    <div
      className={styles.schedule_event}
      style={tint ? { backgroundColor: tint.fill } : undefined}
    >
      <div
        className={styles.schedule_event_bar}
        style={{ backgroundColor: barColor }}
      />
      <div className={styles.schedule_event_content}>
        <div className={styles.schedule_event_main}>
          <span
            className={styles.schedule_event_title}
            style={tint ? { color: tint.ink } : undefined}
          >
            {props.event.title}
          </span>
          {isProgram && registrationStatusCode && (
            <span
              className={cls(styles.schedule_badge, {
                [styles.schedule_badge_open]: registrationStatusCode === "open",
                [styles.schedule_badge_closed]:
                  registrationStatusCode !== "open",
              })}
            >
              {registrationStatus ||
                (registrationStatusCode === "open"
                  ? t("Registration Open")
                  : t("Unavailable"))}
            </span>
          )}
        </div>
        <div className={styles.schedule_event_meta}>
          <span
            className={styles.schedule_event_time}
            style={tint ? { color: tint.muted } : undefined}
          >
            {timeRange}
          </span>
          {paymentLabel && !isScheduleBlock && (
            <span
              className={cls(
                styles.schedule_badge,
                getPaymentStatusClass(paymentStatus, expiresAt),
              )}
            >
              <i
                className={cls(
                  "ico",
                  getPaymentIcon(paymentMethod),
                  styles.schedule_badge_icon,
                )}
              />
              {paymentLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
